/**
 * Tree projection — converts a flat `UniversalSceneNode[]` array (parent-child graph)
 * into a virtualiser-friendly flat list of visible rows.
 *
 * The output respects:
 *   - The caller-supplied `expandedIds` set.
 *   - Depth-first, pre-order traversal (parent before children).
 *   - Per-level display cap of `SCENE_TREE_BUDGETS.MAX_PER_LEVEL` (200).
 *   - A synthetic `show-more` row when the cap is hit.
 *   - Sibling sort by `UniversalSceneNode.sortKey` (ascending).
 *
 * Block 3 refinement — aspect-strict membership (INIT-2026-Q3-SCENE-HIERARCHY):
 *
 *   STRICT RULE:
 *     A node is a member of aspect A iff `node.aspects[A]` is defined.
 *     Nodes that do NOT declare `aspects[A]` are entirely absent from that
 *     aspect's tree — they are neither roots nor children.
 *     This prevents functional-only nodes (disciplines) from leaking into the
 *     spatial tree and vice versa.
 *
 *   ROOT RULE:
 *     Roots(A) = nodes that are members of A AND have `aspects[A].parentId == null`.
 *
 *   CHILD RULE:
 *     childrenOf(node, A) = members of A with `aspects[A].parentId === node.id`.
 *     The legacy top-level `parentId` is NEVER used as a cross-aspect fallback
 *     for nodes that declare an `aspects` object.
 *
 *   LEGACY BACK-COMPAT:
 *     If a node's `aspects` field is absent at runtime (i.e., `node.aspects`
 *     is null/undefined — legacy Tier-1 nodes produced before Block 3 that
 *     predate the aspect-membership contract), the node is treated as a member
 *     of BOTH aspects and its top-level `parentId` is used for placement.
 *     This preserves the pre-Block-3 rendering for live gTwin trees that have
 *     not yet been re-converted. Nodes that DO declare an `aspects` object (even
 *     `aspects: {}`) are governed by the strict rule.
 *
 * Block 1 addition (retained):
 *   An `aspect` parameter selects which parent graph to project.
 *   - `"spatial"` (default): spatial containment — asset→area→elements.
 *   - `"functional"`: functional grouping — asset→discipline→elements.
 *
 * This is a **pure function** — no side effects, no React.
 * O(visible nodes) on every expand/collapse per 50-ui-design-brief.md §3.3.
 *
 * Source: 50-ui-design-brief.md §3.3; 20-two-tier-architecture.md §8.3;
 *         98-adoption-plan.md §Block1, §Block3.
 */

import type { UniversalSceneNode } from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import { SCENE_TREE_BUDGETS } from "./budgets";

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** A node row in the flat-projected list. */
export interface NodeFlatRow {
  readonly kind: "node";
  readonly node: UniversalSceneNode;
  /** 0-based depth (root nodes are 0). */
  readonly depth: number;
  /** True when the node has children (loaded or unloaded). */
  readonly hasChildren: boolean;
  /** True when the node is in the `expandedIds` set. */
  readonly isExpanded: boolean;
}

/**
 * A synthetic row inserted after the last visible sibling when
 * `MAX_PER_LEVEL` has been reached for a given parent.
 */
export interface ShowMoreFlatRow {
  readonly kind: "show-more";
  /** Stable synthetic key: `"show-more-${parentId ?? 'root'}"`. */
  readonly id: string;
  /** Parent node id, or null for root-level overflow. */
  readonly parentId: string | null;
  /** Same depth as the sibling nodes that were capped. */
  readonly depth: number;
  /** Number of nodes not yet shown at this level. */
  readonly remaining: number;
}

export type FlatRow = NodeFlatRow | ShowMoreFlatRow;

/** Aspect selector for the tree projection. */
export type TreeAspect = "spatial" | "functional";

// ─────────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true iff the node's runtime `aspects` field is absent (null/undefined).
 * Used to detect legacy Tier-1 nodes that predate the aspect-membership contract.
 */
function isLegacyNode(node: UniversalSceneNode): boolean {
  // TypeScript declares `aspects` as required, but pre-Block-3 runtime JSON
  // may arrive without the field. Optional chaining guards against that.
  return (node as { aspects?: unknown }).aspects == null;
}

/**
 * Returns true iff the node is a member of the given aspect.
 *
 * STRICT RULE (Block 3):
 *   A node belongs to aspect A iff `node.aspects[A]` is defined.
 *   A node with `aspects: {}` is in neither aspect.
 *
 * LEGACY BACK-COMPAT:
 *   If `node.aspects` is absent at runtime the node predates the contract and
 *   is treated as a member of BOTH aspects (uses top-level `parentId`).
 */
function isMemberOfAspect(
  node: UniversalSceneNode,
  aspect: TreeAspect,
): boolean {
  if (isLegacyNode(node)) return true;
  // A missing aspect may serialize as `null` (Pydantic Optional -> None ->
  // JSON null) OR be omitted (`undefined`). Both mean "not a member" — use
  // `!= null` to catch both, never just `!== undefined`.
  return node.aspects[aspect] != null;
}

/**
 * Returns the effective parentId for a node under the chosen aspect.
 * Call only for nodes already confirmed as members of the aspect.
 *
 * STRICT RULE (Block 3):
 *   Uses `node.aspects[aspect].parentId` exclusively — no cross-aspect fallback.
 *
 * LEGACY BACK-COMPAT:
 *   Nodes with no `aspects` field fall back to the top-level `parentId`.
 */
function getAspectParentId(
  node: UniversalSceneNode,
  aspect: TreeAspect,
): string | null {
  if (isLegacyNode(node)) return node.parentId;
  // Defensive: aspect entry is guaranteed non-null by isMemberOfAspect, but
  // guard anyway so a stray null can never crash the projection.
  return node.aspects[aspect]?.parentId ?? null;
}

/**
 * Builds a lookup map from effectiveParentId → sorted children for O(1) child access.
 *
 * Only nodes that are MEMBERS of the chosen aspect are included (strict rule).
 * Non-members are entirely absent from the map and therefore from the projected tree.
 */
function buildChildrenMap(
  nodes: UniversalSceneNode[],
  aspect: TreeAspect,
): Map<string | null, UniversalSceneNode[]> {
  const map = new Map<string | null, UniversalSceneNode[]>();

  for (const node of nodes) {
    if (!isMemberOfAspect(node, aspect)) continue;

    const effectiveParentId = getAspectParentId(node, aspect);
    const bucket = map.get(effectiveParentId);
    if (bucket) {
      bucket.push(node);
    } else {
      map.set(effectiveParentId, [node]);
    }
  }

  // Sort each bucket by sortKey ascending, then by id for stable sort ties.
  for (const bucket of map.values()) {
    bucket.sort((a, b) => {
      const sk = a.sortKey - b.sortKey;
      return sk !== 0 ? sk : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }

  return map;
}

/** Recursive DFS emitter with offset-based pagination. */
function emitChildren(
  parentId: string | null,
  depth: number,
  expandedIds: Set<string>,
  childrenMap: Map<string | null, UniversalSceneNode[]>,
  out: FlatRow[],
  offsets: ShowMoreOffsets,
): void {
  const siblings = childrenMap.get(parentId);
  if (!siblings || siblings.length === 0) return;

  const cap = SCENE_TREE_BUDGETS.MAX_PER_LEVEL;
  const offset = offsets.get(parentId) ?? 0;
  const visibleEnd = offset + cap;
  const visible = siblings.slice(0, visibleEnd);
  const overflow = siblings.length - visibleEnd;

  for (const node of visible) {
    const hasChildren =
      node.hasChildren || (childrenMap.get(node.id)?.length ?? 0) > 0;
    const isExpanded = expandedIds.has(node.id);

    out.push({
      kind: "node",
      node,
      depth,
      hasChildren,
      isExpanded,
    });

    if (isExpanded && hasChildren) {
      emitChildren(node.id, depth + 1, expandedIds, childrenMap, out, offsets);
    }
  }

  if (overflow > 0) {
    out.push({
      kind: "show-more",
      id: `show-more-${parentId ?? "root"}`,
      parentId,
      depth,
      remaining: overflow,
    });
  }
}

/**
 * Per-parent offset tracking for "Show more" pagination.
 * Maps parentId (or `null` for root-level) to the number of children
 * already shown. The projection slices `siblings[0 .. offset + MAX_PER_LEVEL]`.
 */
export type ShowMoreOffsets = ReadonlyMap<string | null, number>;

/**
 * Projects a flat `UniversalSceneNode[]` array into a virtualiser-ready list.
 *
 * Only nodes that are MEMBERS of the chosen aspect appear in the output.
 * Aspect membership is determined by the presence of `node.aspects[aspect]`
 * (Block 3 strict rule). See module-level JSDoc for the legacy back-compat clause.
 *
 * @param nodes       All nodes in the manifest (any order, linked by aspects).
 * @param expandedIds Set of node ids whose children should be visible.
 * @param aspect      Which parent graph to project. Default "spatial" (back-compat).
 * @param offsets     Per-parent offset for "Show more" pagination. Default empty.
 * @returns           Ordered, depth-first flat list of visible rows.
 */
export function projectTreeToFlatList(
  nodes: UniversalSceneNode[],
  expandedIds: Set<string>,
  aspect: TreeAspect = "spatial",
  offsets: ShowMoreOffsets = new Map(),
): FlatRow[] {
  const out: FlatRow[] = [];
  const childrenMap = buildChildrenMap(nodes, aspect);
  emitChildren(null, 0, expandedIds, childrenMap, out, offsets);
  return out;
}
